/**
 * Row + column helpers for LiveContentTab. Kept in a separate (non-component)
 * module so the tab file only exports its component (React Fast Refresh), and so
 * these pure/near-pure builders are unit-testable in isolation.
 */
import type { AssignedProduct, RosterEntryLive, SoundUsed, LivenessState } from '@discovr/types';

import { Badge } from '../../components/ui/badge';
import type { ColumnDef } from '../../components/common/DataTable';
import { rowDisplayName } from '../utils/rowDisplayName';
import { liveVideoThumbnailUrl, parseYouTubeVideoId } from '../../lib/youtubeThumbnail';
import { describeCount, type CountDisplay } from '../../lib/engagementCount';

// only two real sources after demo data was dropped.
//   - live_content: row backed by live_video.url + statistics
//   - live_pending: roster entry awaiting an admin-set live URL
export type LiveVideoSource = 'live_content' | 'live_pending';

export type LiveVideoRow = {
  key: string;
  creatorId: string;
  creatorName: string;
  source: LiveVideoSource;
  title: string;
  url: string;
  thumbnail?: string;
  contentKind?: string;
  /** This creator's own committed deliverable's content kind — what a pasted
   *  live URL is validated against on the "Add live URL" modal. Distinct from
   *  `contentKind` above (which reflects the PUBLISHED video's actual kind);
   *  this is the pre-publish expectation, present on live_pending rows too. */
  expectedContentKind?: string;
  soundUsed?: SoundUsed | null;
  campaignHasOfficialAudio?: boolean;
  livenessState?: LivenessState;
  /** Engagement counts (shared display rule) for the table cells and the video popup. */
  views?: CountDisplay;
  likes?: CountDisplay;
  comments?: CountDisplay;
  uploadDate?: string;
  embedId: string | null;
  /** Underlying roster entry / creator record. Carried so the video cell
   *  can render the creator's avatar + display name / @handle inline. */
  creator?: Record<string, unknown>;
  /** Products assigned to this creator (Multi-Product Campaigns), resolved.
   *  Rendered read-only in the gated Products column. */
  assignedProducts?: AssignedProduct[];
  /** Admin-set estimated go-live datetime (ISO-8601 UTC). Only meaningful on
   *  a live_pending row — the BE clears it the moment GoLive fires. */
  liveEta?: string;
  /** When this post actually went live (ISO-8601 UTC) — only present on a
   *  published row; live_pending never shows a date (see EtaTimeline). */
  wentLiveAt?: string;
};

/**
 * Whether a live row may trigger an on-demand Official Audio Compliance
 * re-check: it needs a creator id, a campaign that actually set official audio,
 * a published Reel (Official Audio Compliance is Reels-only — a Story/static
 * post/carousel has nothing to scrape, mirrors the BE's audioComplianceReelLookup
 * gate), and a currently-VISIBLE reel. A NOT_VISIBLE post can't be fetched, so a
 * re-check would only churn a doomed scrape that times out on the BE and never
 * yields a new verdict — the stored sound stands. Pure, so the button's guard is
 * unit-testable in isolation; the admin capability itself is checked separately
 * at the call site.
 */
export function canRecheckRowAudio(row: LiveVideoRow): boolean {
  return (
    !!row.creatorId &&
    !!row.campaignHasOfficialAudio &&
    row.contentKind === 'reel' &&
    row.livenessState !== 'NOT_VISIBLE'
  );
}

/**
 * Build LiveVideoRow from a RosterEntryLive. live_video presence is the
 * phase discriminator: present → real video row (status=live), absent
 * → placeholder "awaiting URL" row (status=live_pending). BE returns
 * both intentionally so brands can see who's about to ship.
 */
function rowFromEntry(entry: RosterEntryLive): LiveVideoRow {
  const name = rowDisplayName(entry);
  const stats = entry.live_video?.statistics;
  const url = entry.live_video?.url ?? '';
  // A repost goes live with no live_video, so absence is not "awaiting URL".
  const published = entry.status ? entry.status === 'live' : !!url;
  if (published) {
    return {
      key: `${entry.id}-live-${url}`,
      creatorId: entry.id,
      creatorName: name,
      source: 'live_content',
      title: entry.live_video?.title ?? '',
      url,
      thumbnail: liveVideoThumbnailUrl(entry.live_video) ?? undefined,
      contentKind: entry.live_video?.content_kind,
      expectedContentKind: entry.expected_content_kind,
      soundUsed: entry.sound_used,
      campaignHasOfficialAudio: entry.campaign_has_official_audio,
      livenessState: entry.liveness_state,
      views: describeCount(stats, 'views'),
      likes: describeCount(stats, 'likes'),
      comments: describeCount(stats, 'comments'),
      uploadDate: 'Live',
      embedId: parseYouTubeVideoId(url),
      creator: entry as unknown as Record<string, unknown>,
      assignedProducts: entry.assigned_products,
      wentLiveAt: entry.went_live_at,
    };
  }
  return {
    key: `${entry.id}-pending`,
    creatorId: entry.id,
    creatorName: name,
    source: 'live_pending',
    title: '',
    url: '',
    expectedContentKind: entry.expected_content_kind,
    views: undefined,
    likes: undefined,
    comments: undefined,
    uploadDate: undefined,
    embedId: null,
    creator: entry as unknown as Record<string, unknown>,
    assignedProducts: entry.assigned_products,
    liveEta: entry.live_eta,
  };
}

/**
 * Build rows from BE's /roster/live-content endpoint (ViewLive: live +
 * live_pending). Dedupes against duplicate URLs (defensive — the BE
 * shouldn't emit dupes for one campaign, but historically did).
 */
export function buildRowsFromApiEntries(entries: RosterEntryLive[]): LiveVideoRow[] {
  const rows: LiveVideoRow[] = [];
  const seenUrls = new Set<string>();
  const seenPendingIds = new Set<string>();
  for (const entry of entries) {
    const row = rowFromEntry(entry);
    if (row.source === 'live_content') {
      if (seenUrls.has(row.url)) continue;
      seenUrls.add(row.url);
    } else {
      if (seenPendingIds.has(row.creatorId)) continue;
      seenPendingIds.add(row.creatorId);
    }
    rows.push(row);
  }
  return rows;
}

/**
 * liveProductsColumns — the gated Products column for the live table
 * (Multi-Product Campaigns). Returns the column ONLY when the campaign has >= 2
 * campaign-level products (the global render gate); below that it returns `[]`
 * so 0-1 product campaigns render the table exactly as before. Also hidden for
 * Shopify campaigns — their products come from the Shopify integration, not
 * per-creator campaign-level assignment. Read-only chips (assignment is
 * admin-owned elsewhere); identical for brand and admin.
 */
export function liveProductsColumns(
  campaignProductCount: number,
  shopifyEnabled: boolean,
): ColumnDef<LiveVideoRow>[] {
  if (shopifyEnabled) return [];
  if (campaignProductCount < 2) return [];
  return [
    {
      id: 'products',
      header: 'Products',
      minWidth: 160,
      wrap: true,
      cell: (row: LiveVideoRow) => {
        const items = row.assignedProducts ?? [];
        if (items.length === 0) {
          return <span className="text-caption-regular text-muted-foreground">—</span>;
        }
        return (
          <div className="flex flex-wrap items-center gap-1">
            {items.map((p) => (
              <Badge key={p.product_id} variant="secondary" title={p.notes || p.name}>
                {p.name}
              </Badge>
            ))}
          </div>
        );
      },
    },
  ];
}
